import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PlanPreview = ({ planData, onEdit }) => {
  const getBillingCycleLabel = (cycle) => {
    const labels = {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      semi_annual: 'Semi-Annual',
      annual: 'Annual'
    };
    return labels?.[cycle] || cycle;
  };

  const getAccessLevelLabel = (level) => {
    const labels = {
      basic: 'Basic Access',
      standard: 'Standard Access',
      premium: 'Premium Access',
      elite: 'Elite Access'
    };
    return labels?.[level] || level;
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-heading font-bold text-foreground mb-2">Plan Preview</h2>
        <p className="text-muted-foreground">Review your membership plan before creating it</p>
      </div>

      {/* Plan Card Preview */}
      <div className="max-w-2xl mx-auto bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl border-2 border-primary p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-2xl font-heading font-bold text-foreground mb-2">
              {planData?.planName || 'Plan Name'}
            </h3>
            <p className="text-sm text-muted-foreground">{planData?.description || 'No description'}</p>
          </div>
          {planData?.isFeatured && (
            <span className="px-3 py-1 bg-primary text-primary-foreground rounded-full text-xs font-semibold">
              Featured
            </span>
          )}
        </div>

        <div className="mb-6">
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-bold text-primary">${planData?.price || '0.00'}</span>
            <span className="text-muted-foreground">/ {getBillingCycleLabel(planData?.billingCycle)}</span>
          </div>
          {parseFloat(planData?.setupFee) > 0 && (
            <p className="text-sm text-muted-foreground mt-1">+ ${planData?.setupFee} setup fee</p>
          )}
          {parseInt(planData?.trialPeriodDays) > 0 && (
            <p className="text-sm text-success mt-1">{planData?.trialPeriodDays}-day free trial</p>
          )}
        </div>

        {/* Features */}
        {planData?.features?.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="List" size={18} color="var(--color-primary)" />
              Features
            </h4>
            <div className="space-y-2">
              {planData?.features?.map((feature, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Icon name="Check" size={18} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Benefits */}
        {planData?.benefits?.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Gift" size={18} color="var(--color-accent)" />
              Benefits
            </h4>
            <div className="space-y-2">
              {planData?.benefits?.map((benefit, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Icon name="Star" size={18} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Details */}
        <div className="grid grid-cols-2 gap-4 pt-6 border-t border-border">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Access Level</p>
            <p className="text-sm font-semibold text-foreground">{getAccessLevelLabel(planData?.accessLevel)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Class Credits</p>
            <p className="text-sm font-semibold text-foreground">
              {planData?.classCredits === '-1' || parseInt(planData?.classCredits) === -1
                ? 'Unlimited' : planData?.classCredits ||'0'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Personal Training</p>
            <p className="text-sm font-semibold text-foreground">
              {planData?.personalTrainingSessions || '0'} sessions/month
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Max Members</p>
            <p className="text-sm font-semibold text-foreground">
              {planData?.maxMembers || 'Unlimited'}
            </p>
          </div>
        </div>
      </div>

      {/* Edit Sections */}
      <div className="max-w-2xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
        <Button variant="outline" onClick={() => onEdit('pricing')} iconName="Edit" iconPosition="left">
          Edit Pricing
        </Button>
        <Button variant="outline" onClick={() => onEdit('features')} iconName="Edit" iconPosition="left">
          Edit Features
        </Button>
      </div>

      {/* Plan Status */}
      <div className="max-w-2xl mx-auto p-4 bg-muted/50 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon
              name={planData?.isActive ? 'CheckCircle' : 'XCircle'}
              size={20}
              color={planData?.isActive ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
            />
            <span className="text-sm font-medium text-foreground">
              {planData?.isActive ? 'Active - Visible to members' : 'Inactive - Hidden from members'}
            </span>
          </div>
          {planData?.isFeatured && (
            <span className="text-xs text-primary font-semibold">Featured Plan</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanPreview;